import React, { PropsWithChildren, useMemo } from 'react';
import { prefixer } from 'stylis';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import {
  theme as appTheme,
  ThemeProvider,
  CssBaseline
} from '@rabex-kit/rabex-ui';
import { CacheProvider } from '@emotion/react';
import SnackbarProvider from './components/SnackbarProvider';
import { AppGlobalStyles } from './components/GlobalStyles';

type ThemeMode = 'light' | 'dark';

export const MuiThemeContext = React.createContext({
  toggleColorMode: () => {}
});

// Create rtl cache
const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin]
});

const lsName = 'rabex_theme_mode';

const MuiTheme = ({ children }: PropsWithChildren) => {
  const lsStoredMode = React.useMemo(() => {
    const lsMode = localStorage.getItem(lsName) || 'light';
    return lsMode as ThemeMode;
  }, []);

  const [mode, setMode] = React.useState<ThemeMode>(lsStoredMode);

  const contextValue = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem(lsName, newMode);
          return newMode;
        });
      }
    }),
    []
  );

  const selectedTheme = useMemo(
    () => appTheme({ mode, direction: 'rtl' }),
    [mode]
  );

  return (
    <MuiThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={selectedTheme}>
        <AppGlobalStyles />
        <SnackbarProvider>
          <CacheProvider value={cacheRtl}>
            {children}
            <CssBaseline />
          </CacheProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </MuiThemeContext.Provider>
  );
};

export default MuiTheme;
